// @ts-check
import { syncPagerWithSwiper } from "../pager/pager-swiper-sync.js";

class JobMarketSearch extends HTMLElement {
	#apiEndpoint;
	#emptyResultsContainer;
	#resultSet;
	#resultListContainer;
	#swiperComponent;
	#swiper;
	#pagerSync;
	#elements;

	constructor() {
		super();

		this.#elements = {
			pager: this.querySelector(".JobMarketSearch-pager"),
		};
	}

	connectedCallback() {
		this.#apiEndpoint = this.getAttribute("api-endpoint");

		if (!this.#apiEndpoint) {
			throw new Error("API endpoint is required");
		}

		this.#init();
		this.#addEventListeners();
	}

	#addEventListeners() {
		const form = /** @type {HTMLFormElement} */ (
			this.querySelector(".js-JobMarketSearch-form")
		);
		if (!form) {
			return;
		}

		form.addEventListener("submit", (event) => {
			event.preventDefault();
			const formData = /** @type {any} */ (new FormData(form));
			const queryParams = new URLSearchParams(formData).toString();
			this.#searchJobs(queryParams.toString());
		});
	}

	async #init() {
		await this.#getSwiperInstance();
		this.#searchJobs();
	}

	/**
	 * Get Swiper instance from the cd-swiper component
	 */
	async #getSwiperInstance() {
		this.#swiperComponent = /** @type {any} */ (
			this.querySelector("cd-swiper")
		);
		if (!this.#swiperComponent) return;

		// Ensure cd-swiper custom element is defined (with timeout)
		if (!customElements.get("cd-swiper")) {
			try {
				await Promise.race([
					customElements.whenDefined("cd-swiper"),
					new Promise((_, reject) =>
						setTimeout(
							() =>
								reject(
									new Error(
										"cd-swiper custom element not defined. Is the pattern-swiper library loaded?",
									),
								),
							5000,
						),
					),
				]);
			} catch (error) {
				console.error(`[JobMarketSearch] ${error.message}`);
				return;
			}
		}

		// Wait for swiper to be initialized
		await new Promise((resolve) => {
			const checkSwiper = () => {
				this.#swiper = this.#swiperComponent.swiper;
				if (this.#swiper) {
					resolve(null);
				} else {
					setTimeout(checkSwiper, 50);
				}
			};
			checkSwiper();
		});
	}

	/**
	 * Set up pager sync with Swiper (handles dots, buttons, resize)
	 */
	#syncPager() {
		if (!this.#swiper || !this.#elements.pager) return;

		// Destroy previous sync if re-syncing after new results
		if (this.#pagerSync) {
			this.#pagerSync.destroy();
		}

		this.#pagerSync = syncPagerWithSwiper(this.#swiper, this.#elements.pager, {
			fadeContainer: this.querySelector("cd-swiper"),
		});
	}

	#hideAllContainers() {
		[
			".js-JobMarketSearch-error",
			".js-JobMarketSearch-emptyResults",
			".js-JobMarketSearch-resultsList",
		].forEach((selector) => {
			const el = /** @type {HTMLElement} */ (this.querySelector(selector));
			if (el) {
				el.hidden = true;
			}
		});
	}

	#showFetchError() {
		this.#hideAllContainers();

		const errorContainer = /** @type {HTMLElement} */ (
			this.querySelector(".js-JobMarketSearch-error")
		);

		if (errorContainer) {
			errorContainer.hidden = false;
		}
	}

	#showEmptyResults() {
		this.#hideAllContainers();

		this.#emptyResultsContainer = /** @type {HTMLElement} */ (
			this.querySelector(".js-JobMarketSearch-emptyResults")
		);

		if (!this.#emptyResultsContainer || !this.#emptyResultsContainer.hidden) {
			return;
		}

		this.#emptyResultsContainer.hidden = false;
	}

	async #getDefaultResultSet() {
		try {
			const response = await fetch(`${this.#apiEndpoint}`);

			if (!response.ok) {
				throw new Error(`Error during default fetch: ${response.status}`);
			}

			const data = await response.json();
			return data.items;
		} catch (error) {
			this.#showFetchError();
			console.error(`Error fetching job results: ${error.message}`);
		}
	}

	async #getResultsForQueryParams(queryParams) {
		try {
			const response = await fetch(`${this.#apiEndpoint}?${queryParams}`);

			if (!response.ok) {
				throw new Error(`Error during query params fetch: ${response.status}`);
			}

			const data = await response.json();
			return data.items;
		} catch (error) {
			this.#showFetchError();
			console.error(`Error fetching job results: ${error.message}`);
		}
	}

	async #searchJobs(queryParams) {
		if (!queryParams) {
			this.#resultSet = await this.#getDefaultResultSet();
		} else {
			this.#resultSet = await this.#getResultsForQueryParams(queryParams);
		}

		if (!this.#resultSet || !this.#resultSet.length) {
			this.#showEmptyResults();
			return;
		}

		this.#renderResults();
	}

	/**
	 * @typedef {object} ResultItem
	 * @property {boolean} job_of_the_week - Whether to show premium badge
	 * @property {string} job_logo - Company logo
	 * @property {string} organization - Company name
	 * @property {string} job_title - Job title
	 * @property {string} description - Job description
	 * @property {string} location_names - Job location
	 * @property {string} url - Link to job details
	 */

	/**
	 * @param {ResultItem} resultItem
	 * @returns {Node | undefined}
	 */
	#getResultCard(resultItem) {
		const resultCardTmpl = /** @type {HTMLTemplateElement} */ (
			this.querySelector("#result-card-template")
		);

		if (!resultCardTmpl) {
			return;
		}

		const resultCard = /** @type {HTMLElement} */ (
			resultCardTmpl.content.cloneNode(true)
		);
		const logoImage = /** @type {HTMLImageElement} */ (
			resultCard.querySelector(".js-ResultCard-logoImage")
		);

		if (logoImage && resultItem.job_logo) {
			logoImage.src = resultItem.job_logo;
			logoImage.alt = resultItem.organization;
		} else if (logoImage) {
			logoImage.remove();
		}

		const badge = /** @type {HTMLElement} */ (
			resultCard.querySelector(".js-ResultCard-badge")
		);
		if (badge) {
			badge.hidden = !resultItem.job_of_the_week;
		}

		const title = /** @type {HTMLHeadingElement} */ (
			resultCard.querySelector(".js-ResultCard-title")
		);
		if (title) {
			title.textContent = resultItem.job_title;
		}

		const organization = /** @type {HTMLParagraphElement} */ (
			resultCard.querySelector(".js-ResultCard-organization")
		);
		if (organization && resultItem.organization) {
			organization.textContent = resultItem.organization;
		} else {
			organization?.remove();
		}

		const description = /** @type {HTMLParagraphElement} */ (
			resultCard.querySelector(".js-ResultCard-description")
		);
		if (description && resultItem.description) {
			description.textContent = resultItem.description;
		} else {
			description.remove();
		}

		const locationText = /** @type {HTMLSpanElement} */ (
			resultCard.querySelector(".js-ResultCard-locationText")
		);
		if (locationText) {
			locationText.textContent = resultItem.location_names[0];
		}

		const link = /** @type {HTMLAnchorElement} */ (
			resultCard.querySelector(".js-ResultCard-link")
		);
		const linkLabel = link.querySelector(".Button-label");

		if (link && linkLabel) {
			link.href = resultItem.url;
			link.target = "_blank";
			linkLabel.textContent = resultItem.job_title;
		}

		const swiperSlide =
			/** @type {HTMLElement} */ document.createElement("div");
		swiperSlide.classList.add("swiper-slide", "js-ResultCard--swiperSlide");
		swiperSlide.appendChild(resultCard);
		return swiperSlide;
	}

	#renderResults() {
		if (this.#emptyResultsContainer && !this.#emptyResultsContainer.hidden) {
			this.#emptyResultsContainer.hidden = true;
		}

		this.#resultListContainer = /** @type {HTMLElement} */ (
			this.querySelector(".js-JobMarketSearch-resultsList")
		);

		if (!this.#resultListContainer) {
			return;
		}

		const swiperWrapper =
			this.#resultListContainer.querySelector(".swiper-wrapper");
		if (!swiperWrapper) {
			return;
		}

		const currentResultCards = Array.from(
			swiperWrapper.querySelectorAll(".js-ResultCard--swiperSlide"),
		);

		if (currentResultCards.length) {
			currentResultCards.forEach((resultCard) => {
				resultCard.remove();
			});
		}

		const allJobsSlide = swiperWrapper
			.querySelector(".js-ResultCard--allJobs")
			?.closest(".swiper-slide");

		this.#resultSet.forEach((resultItem) => {
			const resultCard = /** @type {HTMLLIElement} */ (
				this.#getResultCard(resultItem)
			);

			if (resultCard) {
				if (allJobsSlide) {
					swiperWrapper.insertBefore(resultCard, allJobsSlide);
				} else {
					swiperWrapper.appendChild(resultCard);
				}
			}
		});
		this.#resultListContainer.hidden = false;

		// Update swiper after adding new slides and reset to the first slide
		if (this.#swiper) {
			this.#swiper.update();
			this.#swiper.slideTo(0, 0);
			this.#syncPager();
		}
	}
}

customElements.define("beck-job-market-search", JobMarketSearch);
